import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import ReadinessGauge from "@/components/ReadinessGauge";
import { TrendingUp, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasPlan } from "@/lib/plans";
import UpgradeLock from "@/components/UpgradeLock";

const impactColor = (i) => i === "High" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : i === "Medium" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200";

export default function Roadmap() {
  const { user } = useAuth();
  const locked = !hasPlan(user?.plan, "growth");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/roadmap").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (!locked) load(); /* eslint-disable-next-line */ }, [locked]);

  if (locked) return <UpgradeLock required="growth" feature="Growth Roadmap" description="Upgrade to Growth for an AI-built action plan to qualify for larger contracts." />;

  return (
    <div className="space-y-6" data-testid="roadmap-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Growth Roadmap</h1>
          <p className="text-slate-500 mt-1">AI-prioritized actions to qualify for larger contracts.</p>
        </div>
        <button onClick={load} data-testid="regenerate-roadmap-button" className="bg-white border border-slate-200 text-slate-700 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50 transition-colors inline-flex items-center gap-2">
          <Sparkles className="h-4 w-4" /> Regenerate
        </button>
      </div>

      {loading ? (
        <div className="h-64 flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-data">Building your roadmap…</p>
        </div>
      ) : data && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 flex flex-col items-center sticky top-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 self-start mb-3">Current Readiness</p>
              <ReadinessGauge score={data.readiness.score} tier={data.readiness.tier} />
              <p className="text-xs text-slate-400 text-center mt-4">Complete actions below to climb toward Bid-Ready (800+).</p>
            </div>
          </div>
          <div className="lg:col-span-2 space-y-4">
            {(data.actions || []).map((a, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid={`roadmap-action-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-lg bg-emerald-900 text-white flex items-center justify-center shrink-0">
                      <TrendingUp className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold">{a.title}</h3>
                      <p className="text-sm text-slate-600 mt-1">{a.description}</p>
                      <div className="flex items-center gap-2 mt-3">
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{a.category}</span>
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${impactColor(a.impact)}`}>{a.impact} impact</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="font-data text-2xl font-medium text-emerald-600">+{a.points}</span>
                    <p className="text-xs text-slate-400">pts</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
